import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ContentService, HrefResolverService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { AnimateService } from '@ddt-web/dom';
import { SessionStorageKey, SortByAlphaPipe, SortByDatePipe } from '@ddt-web/generic-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first } from 'rxjs/operators';

import { GlsConstants } from '../gls/gls.constants';
import { GlsService } from '../gls/gls.services';
import { GlsApiResponse } from '../gls/models/gls-api-response';
import { SearchRecord, SearchResults } from '../gls/models/search-results';
import { SubscriptionsUtil } from '../gls/utils/subscriptions.util';
import { SubscriptionsConfig } from '../utils/subscription.config';
import { LoaderService } from '../widgets/loader/loader.service';
import { AdditionalButton, FilterItem } from '../widgets/top-panel/top-panel';
import { TopPanelService } from '../widgets/top-panel/top-panel.service';
import { SearchResultsService } from './search-results.service';

@UntilDestroy()
@Component({
	// eslint-disable-next-line
	selector: 'app-subscribed-reports',
	templateUrl: './subscribed-reports.component.html',
	styleUrls: ['./subscribed-reports.component.scss'],
	providers: [TealiumAnalyticsService]
})
export class SubscribedReportsComponent implements OnInit {
	@Input() public apiEndpoint: string; // temporary measure to make the gateway call for subscriptions differently for USP and BFA
	@Input() public optedOut: boolean;

	@Output() public optInOutEvent: EventEmitter<boolean> = new EventEmitter();

	public activeFilter: FilterItem;
	public isModalVisible = false;
	public nativeElement: HTMLElement;
	public noRecordMessageKey: string = SubscriptionsConfig.keys.MYSUBS_DOCS_NO_RECORD_MESSAGE_KEY;
	public records: SearchRecord[] = []; // Holds total record
	public recordsToShow: SearchRecord[] = []; // Holds sliced records
	public screenerUrl: string;
	public selectedRecord: SearchRecord;
	public showDefaultMessage = false;
	public showViewMore = false;
	public supportSubUnsub: boolean;
	public viewMoreLess = false;

	public RESUBSCRIBE: string = GlsConstants.RESUBSCRIBE;
	public UNSUBSCRIBE: string = GlsConstants.UNSUBSCRIBE;

	constructor(
		private elementRef: ElementRef,
		private animateService: AnimateService,
		private topPanelService: TopPanelService,
		private searchResultService: SearchResultsService,
		public loaderService: LoaderService,
		private contentService: ContentService,
		private glsService: GlsService,
		private hrefResolverService: HrefResolverService
	) {
		this.activeFilter = this.topPanelService.getFilterByKey('dateFilter');
	}

	public ngOnInit(): void {
		this.nativeElement = this.elementRef.nativeElement.querySelector('#subscribedReportsWrapper');

		const subUnsubFlag: string = this.contentService.get(SubscriptionsConfig.SUB_UNSUB_FLAG, '');
		if (subUnsubFlag && subUnsubFlag.toUpperCase() === 'Y') {
			this.supportSubUnsub = true;
		}

		if (!this.contentService.get(this.noRecordMessageKey, '')) {
			this.showDefaultMessage = true;
		}

		this.configureTopPanel(SubscriptionsConfig.keys.MYSUBS_DOCS_TAB_LABEL_KEY);
		this.topPanelService.onFilterChange.pipe(untilDestroyed(this)).subscribe((activeFilter: FilterItem) => this.onFilterChange(activeFilter));
	}

	/**
	 * @description This method is called when filter options are selected in top-panel
	 * @ param activeFilter<FilterItem>
	 * @ return    undefined
	 */

	public onFilterChange(activeFilter: FilterItem): void {
		if (activeFilter.key !== this.activeFilter.key) {
			this.activeFilter = activeFilter;
			this.filterData();
		}
	}

	/**
	 * [loadData description]
	 */
	private loadData(): void {
		this.loaderService.setLoader = true;
		this.searchResultService.getSearchResults(this.apiEndpoint).pipe(untilDestroyed(this)).subscribe(
			(data: SearchResults) => {
				sessionStorage.setItem(SessionStorageKey.CsrfToken, data.csrfToken);
				this.loaderService.setLoader = false;
				this.nativeElement.style.removeProperty('height');

				this.optInOutEvent.emit(data.optedOut);
				this.records = SubscriptionsUtil.initData(data.searchRecords);
				// Setting viewMore button condition
				if (this.records.length > SubscriptionsConfig.MAX_RECORDS_AT_A_TIME) {
					this.showViewMore = true;
				}
				// If there is no record found, hide the filter options
				if (this.records.length === 0) {
					this.topPanelService.showSortingPanel = false;
				} else {
					this.topPanelService.showSortingPanel = true;
					this.filterData();
				}
			},
			error => {
				console.error('ReportsComponent -> ngOnInit', error);
				this.loaderService.setLoader = false;
				const data = error;
				if (data.hasOwnProperty('optedOut') && data.optedOut) {
					this.optInOutEvent.emit(true);
				}
				this.nativeElement.style.removeProperty('height');
			},
			() => {
				this.screenerUrl = this.hrefResolverService.getResolvedUrl('screenerDefault');
			}
		);
	}

	public optIn(): void {
		this.loaderService.setLoader = true;
		this.searchResultService.optIn().pipe(first()).subscribe(
			data => {
				this.loadData();
				this.optInOutEvent.emit(false);
			},
			error => {
				console.error('Subscriptions optIn API responded with failure', error);
				this.loaderService.setLoader = false;
			}
		);
	}

	/**
	 * [filterData description]
	 */
	public filterData(): void {
		// Filter the records based on the current set filter
		let records: SearchRecord[] = [];
		switch (this.activeFilter.key) {
			case 'azFilter':
				records = new SortByAlphaPipe().transform(this.records, 'title', true);

				if (this.viewMoreLess === true) {
					this.recordsToShow = records;
				} else {
					this.recordsToShow = records.slice(0, SubscriptionsConfig.MAX_RECORDS_AT_A_TIME);
				}
				break;
			case 'dateFilter':
				records = new SortByDatePipe().transform(this.records, { key: 'publicationDate', order: true });

				if (this.viewMoreLess === true) {
					this.recordsToShow = records;
				} else {
					this.recordsToShow = records.slice(0, SubscriptionsConfig.MAX_RECORDS_AT_A_TIME);
				}
				break;
		}
		this.loaderService.setLoader = false;
	}

	private configureTopPanel(tabKey: string): void {
		if (tabKey === SubscriptionsConfig.keys.MYSUBS_DOCS_TAB_LABEL_KEY) {
			this.topPanelService.showAllFilterOptions();
			this.topPanelService.setActiveFilter(this.activeFilter.key, false);

			const button = new AdditionalButton();
			button.href = SubscriptionsConfig.SUBS_REPORTS_MANAGE_ENDPOINT;
			button.label = SubscriptionsConfig.BUTTON_LABEL;
			this.topPanelService.setAdditionalButton.emit(button);

			if (!this.records.length) {
				// if (this.tabBarService.activeTabHeight > 150) {
				// 	this.nativeElement.style.height = this.tabBarService.activeTabHeight + 'px';
				// }
				this.loadData();
			} else {
				this.topPanelService.showSortingPanel = true;
				this.nativeElement.style.removeProperty('height');
				this.filterData();
			}
		}
	}

	/**
	 * [toggleViewButton description]
	 */
	public toggleViewButton($event): void {
		switch (this.viewMoreLess) {
			case false:
				this.animateService.scrollToTop($event.target, 500);
				this.recordsToShow = this.records; // Displaying all the records
				break;
			case true:
				this.animateService.scrollToTop(this.elementRef.nativeElement, 500);
				this.recordsToShow = this.records.slice(0, SubscriptionsConfig.MAX_RECORDS_AT_A_TIME);
				break;
		}
		this.viewMoreLess = !this.viewMoreLess;
	}

	/**
	 * This function handles the subscribe/unsubscribe functionality
	 * @ param {SearchRecord} record
	 * @ param {string} actionType
	 * @ returns {void}
	 */
	public manageDocumentSubscription(record: SearchRecord, actionType: string): void {
		this.selectedRecord = record;

		if (!record.documentSearchableOnVenue) {
			this.isModalVisible = true;
		} else {
			this.selectedRecord.processing = true;
			this.manageRecord(false, actionType);
		}
	}

	/**
	 * This function handles the subscribe/unsubscribe functionality
	 * @ param {boolean} isModal
	 * @ param {string} actionType
	 * @ returns {void}
	 */
	public manageRecord(isModal: boolean, actionType: string): void {
		if (isModal) {
			this.isModalVisible = false;
			this.selectedRecord.processing = true;
		}
		const currentCsrfToken = sessionStorage.getItem(SessionStorageKey.CsrfToken);
		this.glsService.manageDocumentSubscription(this.selectedRecord.documentId, actionType, currentCsrfToken)
			.pipe(first())
			.subscribe(
				(res: GlsApiResponse) => {
					sessionStorage.setItem(SessionStorageKey.CsrfToken, res.csrfToken);
					const apiResult = res.result[0];

					if (res.success && apiResult.status === GlsConstants.SUCCESS_STATUS) {
						if (apiResult.action === GlsConstants.UNSUBSCRIBE) {
							this.selectedRecord.unSub = true;
						} else if (apiResult.action === GlsConstants.RESUBSCRIBE) {
							this.selectedRecord.unSub = false;
						}
					}
					this.selectedRecord.processing = false;
					console.log('The operation for Subscribe / Unsubscribe completed for ' + this.selectedRecord.title);
				},
				error => {
					this.selectedRecord.processing = false;
					console.log(error);
				}
			);
	}
}
